/// <reference types="vite/client" />

import { createApiClient, isApiError, getApiError, type ApiClient } from '@aia/platform-core';

export { isApiError, getApiError };

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';
const E2E_MODE_KEY = 'e2e_test_mode';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const baseClient = createApiClient({ baseUrl: API_BASE_URL });

function isE2EMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (sessionStorage.getItem(E2E_MODE_KEY) === 'true') return true;
  // Honor the live URL even if sessionStorage was cleared or never set.
  try {
    return new URLSearchParams(window.location.search).get('e2e') === 'true';
  } catch {
    return false;
  }
}

export function enableE2EMode(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(E2E_MODE_KEY, 'true');
}

function authHeaders(): Record<string, string> {
  const token =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY)
      : null;
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (isE2EMode()) {
    headers['X-E2E-Bypass'] = 'true';
  }
  return headers;
}

const AUTH_PAGES = ['/login', '/forgot-password', '/reset-password', '/sso'];

function handleUnauthorized(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  if (!AUTH_PAGES.some((p) => window.location.pathname.startsWith(p))) {
    window.location.href = '/login?session_expired=true';
  }
}

interface SkipAuthOptions {
  skipAuthRedirect?: boolean;
}

async function withAuth<T>(fn: () => Promise<T>, opts?: SkipAuthOptions): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!opts?.skipAuthRedirect && isApiError(err) && err.status === 401) {
      handleUnauthorized();
    }
    throw err;
  }
}

export const api: ApiClient = {
  get: (url, opts) =>
    withAuth(() =>
      baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  post: (url, body, opts) =>
    withAuth(() =>
      baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  put: (url, body, opts) =>
    withAuth(() =>
      baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  patch: (url, body, opts) =>
    withAuth(() =>
      baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  delete: (url, opts) =>
    withAuth(() =>
      baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
};

export const authApi: ApiClient = {
  get: (url, opts) =>
    withAuth(
      () => baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  post: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  put: (url, body, opts) =>
    withAuth(
      () => baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  patch: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  delete: (url, opts) =>
    withAuth(
      () => baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
};
